/**
 * ImportWizard — Import-Wizard für Kontoauszüge (BR-3,
 * architecture-bank-reconciliation.md §4/§10.1).
 *
 * Schritte: (0) AVV-Consent-Gate (nur solange keine gültige Zustimmung vorliegt),
 * (1) Datei wählen → Format-Erkennung, (2) Konto zuordnen/anlegen
 * (IBAN-Mismatch-Abbruch), (3) nur CSV ohne auto-erkanntes Profil:
 * Spalten-Mapping (→ `bank_csv_profiles`), (4) Vorschau mit Duplikat-Zähler
 * → Import (`bankApi.importStatement`).
 *
 * AVV-Consent-Gate (jurist-bank-haftung.md §5/§7.2, Auflage B — fail-closed):
 * Bankumsatzdaten enthalten sensible Fremd-Finanzdaten (Zahlungspartner), daher
 * MUSS der Nutzer der Auftragsverarbeitungsvereinbarung zustimmen, BEVOR er den
 * ersten Kontoauszug hochlädt. Solange `sellers/{uid}.bankAvvAcceptedVersion`
 * nicht der aktuellen {@link BANK_AVV_VERSION} entspricht, rendert der Wizard den
 * Datei-Upload gar nicht erst — statt dessen das versionierte AVV-Dokument mit
 * einer UNVORBELEGTEN Zustimmungs-Checkbox (Kopplungsverbot, Art. 7 Abs. 4
 * DSGVO). Die Zustimmung wird server-persistent geloggt (`saveSellerData` →
 * owner-only `sellers/{uid}`, Rules-enforced) mit Version + Zeitstempel. Muster
 * 1:1 aus `InboundActivation`/`AdvisorConsentDialog` (versioniertes Dokument via
 * `useLegalDocument`, fail-closed bei fehlend/Entwurf). Zusätzlich hält
 * `handleImport` einen harten Guard (kein `importStatement` ohne gültigen
 * Consent) — Defense-in-Depth.
 *
 * CAMT-only-Start (§4): CAMT.053-Dateien überspringen Schritt 3 komplett
 * (kein Mapping nötig); CSV-Dateien mit einer bereits per Header-Signatur
 * erkannten Zuordnung (built-in ODER gespeichertes User-Profil,
 * `findProfileByHeader`) überspringen Schritt 3 ebenfalls.
 *
 * @module ImportWizard
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose, IconFileUpload, IconBank, IconWarning } from '../../icons';
import { ClipLoader } from 'react-spinners';

import {
  listBankAccounts,
  createBankAccount,
  listCsvProfiles,
  saveCsvProfile,
  importStatement,
} from '../../api/bankApi';
import { detectStatementFormat } from '../../utils/bankImport/detectStatementFormat';
import { decodeStatementFile } from '../../utils/bankImport/decodeStatementFile';
import { parseCamt053 } from '../../utils/bankImport/parseCamt053';
import { parseBankCsv } from '../../utils/bankImport/parseBankCsv';
import { findProfileByHeader, computeHeaderSignature } from '../../utils/bankImport/csvProfiles';
import type { ParsedStatement, CsvProfileShape } from '../../utils/bankImport/types';
import type {
  BankAccount,
  BankAccountType,
  BankStatementImport,
  StatementFileFormat,
} from '../../types/bankTypes';
import CsvMappingStep from './CsvMappingStep';
import ImportPreviewStep, { type CsvCoverageConfirmation } from './ImportPreviewStep';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';
import { useAuth } from '../../context/AuthContext';
import { useLegalDocument } from '../tenant/useLegalDocument';
import SimpleMarkdown from '../inbound/SimpleMarkdown';
import {
  BANK_AVV_VERSION,
  BANK_AVV_DOC_PATH,
  BANK_AVV_DRAFT_MARKER,
  hasBankAvvConsent,
} from '../../config/bankReconcileAccess';

/** Storage-Rule-Limit (§7.2): strikt < 10 MiB. */
const MAX_STATEMENT_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type WizardStep = 'avv' | 'file' | 'account' | 'csv-mapping' | 'preview';

export interface ImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful `importStatement` — parent refreshes account/tx lists. */
  onImportComplete?: (result: BankStatementImport) => void;
}

/** Uppercase, whitespace-stripped IBAN comparison helper (mismatch check, §9.4). */
function normalizeIbanForCompare(iban: string): string {
  return iban.toUpperCase().replace(/\s+/g, '');
}

const ImportWizard: React.FC<ImportWizardProps> = ({ isOpen, onClose, onImportComplete }) => {
  const { t } = useTranslation('bank');
  const { sellerData, saveSellerData } = useAuth();

  // AVV-Consent-Gate (Auflage B): gültige Zustimmung für die aktuelle Version?
  // Fail-closed — fehlt/veraltet ⇒ Import gesperrt, Gate wird vorgeschaltet.
  const avvAccepted = hasBankAvvConsent(sellerData?.bankAvvAcceptedVersion);

  const [step, setStep] = useState<WizardStep>('file');

  // AVV-Gate-Zustand
  const [avvChecked, setAvvChecked] = useState(false); // UNVORBELEGT (Kopplungsverbot)
  const [avvSaving, setAvvSaving] = useState(false);
  const [avvError, setAvvError] = useState<string | null>(null);

  // Step 1 — file
  const [fileName, setFileName] = useState<string | null>(null);
  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [decodedText, setDecodedText] = useState<string | null>(null);
  const [format, setFormat] = useState<StatementFileFormat | 'unknown' | null>(null);
  const [parsed, setParsed] = useState<ParsedStatement | null>(null);
  const [csvProfileId, setCsvProfileId] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Step 2 — account
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountSaving, setNewAccountSaving] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Step 4 — import
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<BankStatementImport | null>(null);

  const resetAll = () => {
    // AVV-Gate zuerst: ohne gültige Zustimmung startet der Wizard im Consent-
    // Schritt, sonst direkt bei der Datei-Auswahl (Bestandsverhalten).
    setStep(avvAccepted ? 'file' : 'avv');
    setAvvChecked(false);
    setAvvSaving(false);
    setAvvError(null);
    setFileName(null);
    setArrayBuffer(null);
    setDecodedText(null);
    setFormat(null);
    setParsed(null);
    setCsvProfileId(null);
    setFileLoading(false);
    setFileError(null);
    setAccounts([]);
    setAccountError(null);
    setShowNewAccountForm(false);
    setSelectedAccountId(null);
    setImportLoading(false);
    setImportError(null);
    setImportResult(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetAll();
    setAccountsLoading(true);
    listBankAccounts()
      .then((list) => setAccounts(list.filter((a) => !a.isArchived)))
      .catch((err: any) => {
        logger.error(err, 'bank accounts load failed (import wizard)');
        setAccountError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.account.loadFailed' }));
      })
      .finally(() => setAccountsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Step 0: AVV-Consent-Gate (Auflage B) ────────────────────────────────────

  /**
   * Erfasst die AVV-Zustimmung server-persistent (owner-only `sellers/{uid}` via
   * `saveSellerData` → Firestore-Rules-enforced) mit Version + ISO-Zeitstempel und
   * schaltet danach die Datei-Auswahl frei. Fehler blocken den Fortschritt
   * (fail-closed) — ohne persistierten Consent kein Upload.
   */
  const handleAvvAccept = async () => {
    if (!avvChecked || avvSaving) return;
    setAvvSaving(true);
    setAvvError(null);
    try {
      await saveSellerData({
        bankAvvAcceptedVersion: BANK_AVV_VERSION,
        bankAvvAcceptedAt: new Date().toISOString(),
      });
      setStep('file');
    } catch (err: any) {
      logger.error(err, 'bank AVV consent persist failed');
      setAvvError(mapErrorToUserMessage(err, t, { fallbackKey: 'avv.saveFailed' }));
    } finally {
      setAvvSaving(false);
    }
  };

  // ── Step 1: file select ────────────────────────────────────────────────────

  const handleFileSelected = async (file: File) => {
    setFileError(null);
    if (file.size >= MAX_STATEMENT_FILE_SIZE_BYTES) {
      setFileError(t('wizard.file.tooLarge', 'Die Datei ist zu groß (maximal 10 MB).'));
      return;
    }

    setFileLoading(true);
    try {
      const buf = await file.arrayBuffer();
      const text = decodeStatementFile(buf);
      const detected = detectStatementFormat(text.slice(0, 2000));

      if (detected === 'unknown') {
        setFileError(t('wizard.file.unknownFormat', 'Dateiformat nicht erkannt (weder CAMT.053 noch CSV).'));
        return;
      }
      if (detected === 'mt940') {
        setFileError(t('wizard.file.mt940Unsupported', 'MT940-Dateien werden noch nicht unterstützt. Bitte CAMT.053 oder CSV exportieren.'));
        return;
      }

      setFileName(file.name);
      setArrayBuffer(buf);
      setDecodedText(text);
      setFormat(detected);

      if (detected === 'camt053') {
        const result = parseCamt053(text);
        setParsed(result);
        setCsvProfileId(null);
      } else {
        // CSV: try to auto-match a saved/built-in profile by header signature.
        const headerLine = (text.split(/\r?\n/, 1)[0] ?? '').trim();
        let matched: CsvProfileShape | null = null;
        let matchedUserProfileId: string | null = null;
        try {
          const userProfiles = await listCsvProfiles();
          matched = findProfileByHeader(headerLine, userProfiles);
          if (matched) {
            const signature = computeHeaderSignature(headerLine);
            matchedUserProfileId = userProfiles.find((p) => p.headerSignature === signature)?.id ?? null;
          }
        } catch {
          // Profile-Laden fehlgeschlagen (z.B. offline) — fällt auf den Mapping-Schritt zurück.
          matched = null;
        }

        if (matched) {
          try {
            const result = parseBankCsv(text, matched);
            setParsed(result);
            // Kein User-Treffer ⇒ built-in (v1: BUILTIN_CSV_PROFILES ist leer,
            // dieser Zweig ist daher aktuell unerreichbar — s. csvProfiles.ts).
            setCsvProfileId(matchedUserProfileId ?? `builtin:${headerLine}`);
          } catch {
            // Erkanntes Profil passt doch nicht (z.B. Spalten-Drift) — Mapping-Schritt.
            setParsed(null);
            setCsvProfileId(null);
          }
        } else {
          setParsed(null);
          setCsvProfileId(null);
        }
      }

      setStep('account');
    } catch (err: any) {
      logger.error(err, 'bank statement file read failed');
      setFileError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.file.readFailed' }));
    } finally {
      setFileLoading(false);
    }
  };

  // ── Step 2: account assignment ─────────────────────────────────────────────

  const advanceAfterAccount = (accountId: string) => {
    setSelectedAccountId(accountId);
    if (format === 'csv' && !parsed) {
      setStep('csv-mapping');
    } else {
      setStep('preview');
    }
  };

  const handleSelectAccount = (account: BankAccount) => {
    setAccountError(null);
    if (parsed?.accountIban && account.iban) {
      if (normalizeIbanForCompare(parsed.accountIban) !== normalizeIbanForCompare(account.iban)) {
        setAccountError(
          t(
            'wizard.account.ibanMismatch',
            'Die IBAN in der Datei ({{fileIban}}) stimmt nicht mit dem gewählten Konto ({{accountIban}}) überein. Bitte das richtige Konto wählen oder ein neues Konto anlegen.',
            { fileIban: parsed.accountIban, accountIban: account.iban }
          )
        );
        return;
      }
    }
    advanceAfterAccount(account.id);
  };

  const handleCreateAccount = async (fields: {
    displayName: string;
    accountType: BankAccountType;
    iban: string;
    bic: string;
    bankName: string;
  }) => {
    setAccountError(null);
    setNewAccountSaving(true);
    try {
      const newId = await createBankAccount({
        displayName: fields.displayName,
        accountType: fields.accountType,
        // BR-18-Korrektur (§5.2/§6.1): IBAN ist für ALLE Kontotypen optional —
        // Kreditkarten-Konten werden NICHT mehr hart auf null gezwungen.
        iban: fields.iban.trim() || null,
        bic: fields.bic.trim() || null,
        bankName: fields.bankName.trim() || null,
        currency: 'EUR',
        isArchived: false,
      });
      const refreshed = await listBankAccounts();
      setAccounts(refreshed.filter((a) => !a.isArchived));
      const created = refreshed.find((a) => a.id === newId);
      setShowNewAccountForm(false);
      if (created) {
        handleSelectAccount(created);
      } else {
        advanceAfterAccount(newId);
      }
    } catch (err: any) {
      logger.error(err, 'bank account create failed');
      setAccountError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.account.createFailed' }));
    } finally {
      setNewAccountSaving(false);
    }
  };

  // ── Step 4: import ──────────────────────────────────────────────────────────

  /**
   * MA-2 (architecture-mahn-automatik.md §6.3): `csvCoverage` is only ever
   * set for CSV imports (the confirmation UI in `ImportPreviewStep` is
   * CSV-only) — CAMT files already carry their period in `parsed.coverageFrom/To`
   * (from `Stmt/FrToDt`, `parseCamt053`). When present it is merged onto
   * `parsed` before `importStatement`, which writes `parsed.coverageFrom/To`
   * 1:1 onto the persisted `BankStatementImport` doc.
   */
  const handleImport = async (csvCoverage?: CsvCoverageConfirmation) => {
    if (!selectedAccountId || !parsed || !arrayBuffer || !fileName || !format || format === 'unknown') {
      return;
    }
    // Defense-in-Depth (Auflage B): harter Consent-Guard direkt vor dem Upload —
    // selbst wenn der Gate-Schritt umgangen würde, kein `importStatement` ohne
    // gültige, server-persistierte AVV-Zustimmung (fail-closed).
    if (!avvAccepted) {
      setImportError(t('avv.required'));
      return;
    }
    setImportError(null);
    setImportLoading(true);
    try {
      const parsedWithCoverage: ParsedStatement =
        format === 'csv' && csvCoverage
          ? { ...parsed, coverageFrom: csvCoverage.coverageFrom, coverageTo: csvCoverage.coverageTo }
          : parsed;
      const result = await importStatement({
        accountId: selectedAccountId,
        filename: fileName,
        fileFormat: format,
        csvProfileId,
        rawFile: arrayBuffer,
        parsed: parsedWithCoverage,
      });
      setImportResult(result);
      onImportComplete?.(result);
    } catch (err: any) {
      logger.error(err, 'bank statement import failed');
      setImportError(mapErrorToUserMessage(err, t, { fallbackKey: 'wizard.preview.importFailed' }));
    } finally {
      setImportLoading(false);
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const stepLabels: Record<WizardStep, string> = {
    avv: t('avv.stepLabel', 'Datenschutz'),
    file: t('wizard.step.file', 'Datei'),
    account: t('wizard.step.account', 'Konto'),
    'csv-mapping': t('wizard.step.csvMapping', 'Zuordnung'),
    preview: t('wizard.step.preview', 'Vorschau'),
  };
  // Der AVV-Gate-Schritt erscheint bewusst NICHT im Fortschritts-Breadcrumb —
  // er ist ein einmaliger Vorschalt-Gate, kein Import-Ablaufschritt.
  const stepOrder: WizardStep[] = ['file', 'account', 'csv-mapping', 'preview'];
  const visibleSteps = stepOrder.filter((s) => s !== 'csv-mapping' || format === 'csv');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-surface border border-hairline rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <IconFileUpload className="text-accent-ink" />
            {t('wizard.title', 'Kontoauszug importieren')}
          </div>
          <button type="button" onClick={handleClose} className="text-muted hover:text-primary">
            <IconClose />
          </button>
        </div>

        {step !== 'avv' && (
          <div className="flex items-center gap-2 px-6 pt-4 text-xs text-faint">
            {visibleSteps.map((s, idx) => (
              <React.Fragment key={s}>
                {idx > 0 && <span className="text-gray-700">→</span>}
                <span className={s === step ? 'text-accent-ink font-medium' : ''}>{stepLabels[s]}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="p-6">
          {step === 'avv' && (
            <BankAvvConsentStep
              checked={avvChecked}
              onCheckedChange={(v) => {
                setAvvChecked(v);
                setAvvError(null);
              }}
              onAccept={handleAvvAccept}
              onCancel={handleClose}
              saving={avvSaving}
              error={avvError}
            />
          )}

          {step === 'file' && (
            <div className="space-y-4">
              <label
                className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  fileLoading ? 'opacity-60 cursor-default' : 'border-strong hover:border-indigo-500 hover:bg-[rgb(var(--surface-raised-rgb)/0.5)]'
                }`}
              >
                <input
                  type="file"
                  accept=".xml,.csv,.txt"
                  className="hidden"
                  disabled={fileLoading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelected(file);
                    e.target.value = '';
                  }}
                />
                {fileLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <ClipLoader color="#6366f1" size={32} />
                    <p className="text-sm text-accent-ink">{t('wizard.file.loading', 'Datei wird gelesen …')}</p>
                  </div>
                ) : (
                  <div className="text-muted">
                    <IconFileUpload size={30} className="mx-auto text-accent-ink mb-3" />
                    <p className="text-primary font-medium">{t('wizard.file.title', 'CAMT.053- oder CSV-Datei hochladen')}</p>
                    <p className="text-sm mt-1">{t('wizard.file.hint', 'Klicken zum Auswählen (max. 10 MB)')}</p>
                  </div>
                )}
              </label>

              {fileError && (
                <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)] flex items-start gap-2">
                  <IconWarning className="mt-0.5 flex-shrink-0" />
                  <span>{fileError}</span>
                </div>
              )}
            </div>
          )}

          {step === 'account' && (
            <AccountStep
              accounts={accounts}
              accountsLoading={accountsLoading}
              error={accountError}
              onSelect={handleSelectAccount}
              showNewAccountForm={showNewAccountForm}
              onToggleNewAccountForm={() => setShowNewAccountForm((v) => !v)}
              onCreateAccount={handleCreateAccount}
              newAccountSaving={newAccountSaving}
              suggestedIban={parsed?.accountIban ?? null}
              onBack={() => setStep('file')}
            />
          )}

          {step === 'csv-mapping' && decodedText && arrayBuffer && fileName && (
            <CsvMappingStep
              initialText={decodedText}
              onRedecode={(encoding) => decodeStatementFile(arrayBuffer, encoding)}
              suggestedProfileName={fileName.replace(/\.[^.]+$/, '')}
              onCancel={() => setStep('account')}
              onConfirm={async ({ profile, parsed: parsedResult, profileName, headerLine }) => {
                const headerSignature = computeHeaderSignature(headerLine);
                const profileId = await saveCsvProfile({
                  name: profileName,
                  headerSignature,
                  delimiter: profile.delimiter,
                  encoding: profile.encoding,
                  decimalStyle: profile.decimalStyle,
                  dateFormat: profile.dateFormat,
                  skipLeadingRows: profile.skipLeadingRows,
                  columnMap: profile.columnMap,
                });
                setCsvProfileId(profileId);
                setParsed(parsedResult);
                setStep('preview');
              }}
            />
          )}

          {step === 'preview' && parsed && selectedAccountId && format && format !== 'unknown' && (
            <ImportPreviewStep
              parsed={parsed}
              accountId={selectedAccountId}
              fileFormat={format}
              onBack={() => setStep(format === 'csv' && csvProfileId === null ? 'csv-mapping' : 'account')}
              onImport={handleImport}
              loading={importLoading}
              error={importError}
              result={importResult}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Step 0 subcomponent: AVV-Consent-Gate (Auflage B) ────────────────────────

interface BankAvvConsentStepProps {
  /** Zustand der UNVORBELEGTEN Zustimmungs-Checkbox (Kopplungsverbot). */
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Persistiert die Zustimmung und schaltet die Datei-Auswahl frei. */
  onAccept: () => void;
  /** Bricht den gesamten Wizard ab (kein Consent gespeichert). */
  onCancel: () => void;
  /** True, während `saveSellerData` läuft (Buttons/Checkbox gesperrt). */
  saving: boolean;
  /** Fehlertext, falls die Consent-Persistenz fehlschlug. */
  error: string | null;
}

/**
 * AVV-Consent-Gate vor dem ersten Import (jurist-bank-haftung.md §5/§7.2).
 * Zeigt das versionierte, operator-verfasste AVV-Dokument (fail-closed bei
 * fehlend/Entwurf via {@link useLegalDocument}) und verlangt eine UNVORBELEGTE
 * Zustimmung, bevor `onAccept` aktiv wird. Der eigentliche Persistenz-Write läuft
 * in der einbettenden Wizard-Komponente. Präsentational analog
 * `AdvisorConsentDialog` — DE-Text ist verbindlich, das Dokument wird nur
 * gelesen, nie verändert.
 */
const BankAvvConsentStep: React.FC<BankAvvConsentStepProps> = ({
  checked,
  onCheckedChange,
  onAccept,
  onCancel,
  saving,
  error,
}) => {
  const { t } = useTranslation('bank');
  const docState = useLegalDocument(BANK_AVV_DOC_PATH, BANK_AVV_DRAFT_MARKER);

  const docReady = docState.kind === 'ready';
  const docBlocked = docState.kind === 'missing' || docState.kind === 'draft';
  const canAccept = docReady && checked && !saving;

  return (
    <div className="space-y-4" data-testid="bank-avv-gate">
      <div>
        <h3 className="text-base font-semibold text-primary">{t('avv.heading')}</h3>
        <p className="mt-1 text-sm text-muted">{t('avv.intro')}</p>
      </div>

      {/* Fehlt/Entwurf → fail-closed-Banner, Zustimmung gesperrt. */}
      {docBlocked && (
        <div
          className="flex items-start gap-2 rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)]"
          role="status"
          data-testid="bank-avv-blocked"
        >
          <IconWarning className="mt-0.5 flex-shrink-0" />
          <span>{docState.kind === 'draft' ? t('avv.draft') : t('avv.notReleased')}</span>
        </div>
      )}

      {/* AVV-Dokument (read-only, operator-verfasst). */}
      <div
        className="max-h-56 overflow-y-auto rounded-lg border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
        data-testid="bank-avv-doc"
      >
        {docState.kind === 'ready' || docState.kind === 'draft' ? (
          <SimpleMarkdown text={docState.text} />
        ) : docState.kind === 'loading' ? (
          t('avv.loading')
        ) : (
          t('avv.notReleased')
        )}
      </div>

      {/* UNVORBELEGTE Zustimmungs-Checkbox (Kopplungsverbot, Art. 7 Abs. 4 DSGVO). */}
      <label className="flex items-start gap-2 text-sm text-secondary">
        <input
          type="checkbox"
          checked={checked}
          disabled={!docReady || saving}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          data-testid="bank-avv-checkbox"
        />
        <span>{t('avv.checkboxLabel')}</span>
      </label>

      {error && (
        <p className="text-sm text-[var(--tone-danger-fg)]" data-testid="bank-avv-error">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm text-muted hover:text-primary disabled:opacity-50"
          data-testid="bank-avv-cancel"
        >
          {t('wizard.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={onAccept}
          disabled={!canAccept}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="bank-avv-accept"
        >
          {saving ? t('avv.saving') : t('avv.acceptButton')}
        </button>
      </div>
    </div>
  );
};

// ─── Step 2 subcomponent (kept in this file — small, tightly coupled to wizard state) ──

interface AccountStepProps {
  accounts: BankAccount[];
  accountsLoading: boolean;
  error: string | null;
  onSelect: (account: BankAccount) => void;
  showNewAccountForm: boolean;
  onToggleNewAccountForm: () => void;
  onCreateAccount: (fields: {
    displayName: string;
    accountType: BankAccountType;
    iban: string;
    bic: string;
    bankName: string;
  }) => void;
  newAccountSaving: boolean;
  suggestedIban: string | null;
  onBack: () => void;
}

const AccountStep: React.FC<AccountStepProps> = ({
  accounts,
  accountsLoading,
  error,
  onSelect,
  showNewAccountForm,
  onToggleNewAccountForm,
  onCreateAccount,
  newAccountSaving,
  suggestedIban,
  onBack,
}) => {
  const { t } = useTranslation('bank');
  const [displayName, setDisplayName] = useState('');
  const [accountType, setAccountType] = useState<BankAccountType>('giro');
  const [iban, setIban] = useState(suggestedIban ?? '');
  const [bic, setBic] = useState('');
  const [bankName, setBankName] = useState('');

  const inputClass =
    'w-full bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:border-indigo-500';

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)] flex items-start gap-2">
          <IconWarning className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {accountsLoading ? (
        <div className="flex justify-center py-6">
          <ClipLoader color="#6366f1" size={28} />
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.length === 0 && !showNewAccountForm && (
            <p className="text-sm text-muted">{t('wizard.account.none', 'Noch kein Konto angelegt.')}</p>
          )}
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => onSelect(account)}
              className="w-full flex items-center justify-between bg-surface-raised hover:bg-surface-inset border border-hairline rounded-lg px-4 py-3 text-left transition-colors"
            >
              <span className="flex items-center gap-2 text-primary text-sm">
                <IconBank className="text-accent-ink" />
                {account.displayName}
              </span>
              <span className="text-xs text-faint">{account.iban ?? t('wizard.account.noIban', 'ohne IBAN')}</span>
            </button>
          ))}
        </div>
      )}

      {!showNewAccountForm ? (
        <button
          type="button"
          onClick={onToggleNewAccountForm}
          className="text-sm text-accent-ink hover:text-accent-ink"
        >
          + {t('wizard.account.newAccount', 'Neues Konto anlegen')}
        </button>
      ) : (
        <div className="border border-hairline rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              {t('wizard.account.field.displayName', 'Bezeichnung *')}
            </label>
            <input
              type="text"
              className={inputClass}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('wizard.account.displayNamePlaceholder', 'z.B. Sparkasse Geschäftskonto')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                {t('wizard.account.field.accountType', 'Typ')}
              </label>
              <select
                className={inputClass}
                value={accountType}
                onChange={(e) => setAccountType(e.target.value as BankAccountType)}
              >
                <option value="giro">{t('wizard.account.type.giro', 'Girokonto')}</option>
                <option value="kreditkarte">{t('wizard.account.type.kreditkarte', 'Kreditkarte')}</option>
                <option value="sonstig">{t('wizard.account.type.sonstig', 'Sonstiges')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                {t('wizard.account.field.iban', 'IBAN')}
              </label>
              <input
                type="text"
                className={inputClass}
                value={iban}
                placeholder="DE…"
                onChange={(e) => setIban(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                {t('wizard.account.field.bic', 'BIC')}
              </label>
              <input type="text" className={inputClass} value={bic} onChange={(e) => setBic(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                {t('wizard.account.field.bankName', 'Bank')}
              </label>
              <input type="text" className={inputClass} value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onToggleNewAccountForm} className="px-3 py-1.5 text-sm text-muted hover:text-primary">
              {t('wizard.cancel', 'Abbrechen')}
            </button>
            <button
              type="button"
              disabled={newAccountSaving || !displayName.trim()}
              onClick={() => onCreateAccount({ displayName, accountType, iban, bic, bankName })}
              className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {t('wizard.account.createAndContinue', 'Anlegen & weiter')}
            </button>
          </div>
        </div>
      )}

      <div className="pt-2">
        <button type="button" onClick={onBack} className="px-4 py-2 text-sm text-muted hover:text-primary">
          {t('wizard.back', 'Zurück')}
        </button>
      </div>
    </div>
  );
};

export default ImportWizard;
